import React, { useMemo, useState } from "react";
import { Row, Col, Form, Select, Input, Button, Collapse, Spin, DatePicker } from "antd";
import type { FormInstance } from "antd";
import { useAppSelector } from "../../..";
import type { AnyObject, DictionaryDto } from "../../../lib/interfaces";
import { checkIsLoading, displayFormat } from "../../../lib/utils";
import MultipleSelect from "../../common/MultipleSelect";
import { IClientType, ISearchFilterField } from "../../../lib/types";
import { BulkFiltersToggler } from "../../BulkFiltersToggler/BulkFiltersToggler";
import { ActiveSearchFormItem, AgreementsSearchFormItem } from "../../BulkSearchItems";
import "./PaymentsSearchFilters.less";
import { shortNames } from "../../../lib/const/shortsNames";

const layout = {
  labelCol: { span: 8 },
  wrapperCol: { span: 16 },
};

interface ComponentProps {
  form: FormInstance;
  defaultSearchParams: AnyObject;
  onSearch: (values: AnyObject) => void;
  filtersCollapseState: string;
  setFiltersCollapsState: React.Dispatch<React.SetStateAction<string>>;
  resetSearchPaymentsAndResetSelectedRows: () => void;
}

export const PaymentsSearchFilters = (props: ComponentProps) => {
  const {
    form,
    defaultSearchParams,
    onSearch,
    filtersCollapseState,
    setFiltersCollapsState,
    resetSearchPaymentsAndResetSelectedRows,
  } = props;

  const { paymentStatusesLoading } = useAppSelector((state) => state.rtkDictionaries);
  const { tenant } = useAppSelector((state) => state.tenants);

  const paymentStatuses = (useAppSelector(
    (state) => state.rtkDictionaries.paymentStatuses
  ) as unknown) as DictionaryDto[];

  const [clientType, setClientType] = useState<IClientType>("ORGANIZATION");

  const allFields: ISearchFilterField[] = useMemo(
    () => [
      {
        label: "Клиент",
        fieldElement: (
          <AgreementsSearchFormItem
            name="accountIds"
            multiselect
            combineAgreements
            clientType={clientType}
            setClientType={setClientType}
            onChange={() => {
              form.setFieldsValue({
                "organization.property.stringValue": undefined,
                "subscriber.externalId": undefined,
              });
            }}
          />
        ),
        visible: true,
      },
      {
        label: "Дата платежа",
        fieldElement: (
          <Form.Item label="Дата платежа" name="datePay">
            <DatePicker.RangePicker
              format={displayFormat}
              style={{ width: "100%" }}
              allowClear={false}
            />
          </Form.Item>
        ),
        visible: true,
      },
      {
        label: "Статус платежа",
        fieldElement: (
          <Form.Item dependencies={["paymentLefts"]} label="Статус платежа">
            {({ getFieldValue }) => {
              const paymentLefts = getFieldValue("paymentLefts");
              return (
                <Form.Item name="includePaymentStatusIds" style={{ marginBottom: 0 }}>
                  <MultipleSelect
                    allValues={paymentStatuses.map(({ id }) => id)}
                    placeholder="Выберите статус"
                    maxTagCount={3}
                    disabled={Boolean(paymentLefts)}
                    showArrow
                    loading={checkIsLoading(paymentStatusesLoading)}
                    allowClear
                  >
                    {paymentStatuses.map((option) => (
                      <Select.Option key={option.id} value={option.id}>
                        {option.name}
                      </Select.Option>
                    ))}
                  </MultipleSelect>
                </Form.Item>
              );
            }}
          </Form.Item>
        ),
        visible: true,
      },
      {
        label: "Номер договора",
        fieldElement: (
          <Form.Item label="Номер договора" name="agreement.agreementNumberLikeAny">
            <Input allowClear />
          </Form.Item>
        ),
        visible: true,
      },
      {
        label: "Дата обработки",
        fieldElement: (
          <Form.Item label="Дата обработки" name="dateConfirm">
            <DatePicker.RangePicker format={displayFormat} style={{ width: "100%" }} allowClear />
          </Form.Item>
        ),
        visible: true,
      },
      {
        label: "Остаток платежа",
        fieldElement: (
          <Form.Item name="paymentLefts" label="Остаток платежа">
            <Select
              allowClear
              onChange={(paymentLefts) => {
                form.setFieldsValue({ includePaymentStatusIds: paymentLefts ? [] : [2] });
              }}
            >
              <Select.Option key={1} value="onlyWithLefts">
                Доступно к распределению
              </Select.Option>
              <Select.Option key={2} value="onlyWithLeftsAndNotFullyPaidBills">
                Доступно к распределению (неопл. счета)
              </Select.Option>
            </Select>
          </Form.Item>
        ),
        visible: true,
      },
      {
        label: "ID платежей",
        fieldElement: (
          <Form.Item
            label="ID платежей"
            name="paymentIds"
            className="form-item-required__hidden-error__mb0"
            rules={[
              {
                validator: (x, value) => {
                  if (value && /[^0-9,]/.test(value)) {
                    return Promise.reject();
                  }
                  return Promise.resolve();
                },
                message: "",
              },
            ]}
          >
            <Input allowClear placeholder='Введите ID платежей через ","' />
          </Form.Item>
        ),
        visible: true,
      },
      {
        label: tenant?.id === 1 ? "БИН" : "ИНН",
        fieldElement: (
          <ActiveSearchFormItem
            name="organization.property.stringValue"
            onSelect={(props) => {
              form.setFieldsValue({
                accountIds: [props.accountId],
                "subscriber.externalId": undefined,
              });
            }}
            clientType={clientType}
          />
        ),
        visible: false,
      },
      {
        label: "Тех. ID",
        labelTitle: "Технический ID",
        fieldElement: (
          <ActiveSearchFormItem
            name="subscriber.externalId"
            onSelect={(props) => {
              form.setFieldsValue({
                accountIds: [props.accountId],
                "organization.property.stringValue": undefined,
              });
            }}
            clientType={clientType}
          />
        ),
        visible: true,
      },
    ],
    [paymentStatuses, paymentStatusesLoading, tenant, clientType]
  );

  const [fields, setFields] = useState<ISearchFilterField[]>(allFields);

  const toggleFiltersCollapseState = () => {
    const newState = filtersCollapseState === "unfolded" ? "folded" : "unfolded";
    setFiltersCollapsState(newState);
  };

  const getPanelHeader = () =>
    filtersCollapseState === "unfolded" ? (
      ""
    ) : (
      <div className="bulk__panel_header">Показать фильтры</div>
    );

  return (
    <div className="payments-search-filters">
      <div className="inner-row">
        <Collapse
          defaultActiveKey="1"
          onChange={toggleFiltersCollapseState}
          expandIconPosition="right"
          className="filter-collapse"
        >
          <Collapse.Panel header={getPanelHeader()} key="1">
            <Form
              form={form}
              labelAlign="left"
              initialValues={defaultSearchParams}
              {...layout}
              onFinish={onSearch}
            >
              <Row justify="end">
                <BulkFiltersToggler
                  allFields={allFields}
                  setFields={setFields}
                  filterName="bulk-payments"
                />
              </Row>
              <Row gutter={50}>
                {fields
                  .filter(({ visible }) => visible)
                  .map(({ fieldElement }, idx) => (
                    <Col span={8} key={idx}>
                      {fieldElement}
                    </Col>
                  ))}
              </Row>
              <Form.Item wrapperCol={{ span: 24 }} style={{ textAlign: "right", marginBottom: 0 }}>
                <Button onClick={resetSearchPaymentsAndResetSelectedRows}>Сброс параметров</Button>
                <Button htmlType="submit" type="primary">
                  Применить
                </Button>
              </Form.Item>
            </Form>
          </Collapse.Panel>
        </Collapse>
      </div>
    </div>
  );
};
